'use client';

import React, { useEffect, useState } from 'react';
import { X, Download, Share2 } from 'lucide-react';

type Platform = 'ios' | 'android' | null;
type BeforeInstallPromptEvent = Event & {
  prompt: () => void;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function getPlatform(): Platform {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) && !(window as unknown as Record<string, unknown>).MSStream) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return null;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function InstallBanner() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<Platform>(null);
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (isStandalone()) return;
    if (typeof localStorage !== 'undefined' && localStorage.getItem('sw-install-dismissed')) return;

    const p = getPlatform();
    if (!p) return;
    setPlatform(p);

    if (p === 'android') {
      const handler = (e: Event) => {
        e.preventDefault();
        setPrompt(e as BeforeInstallPromptEvent);
        setTimeout(() => setVisible(true), 3000);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }

    if (p === 'ios') {
      setTimeout(() => setVisible(true), 3000);
    }
  }, []);

  function dismiss() {
    setVisible(false);
    localStorage.setItem('sw-install-dismissed', '1');
  }

  async function install() {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') dismiss();
    else { setPrompt(null); setVisible(false); }
  }

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] md:hidden animate-slide-up"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="absolute inset-0 bg-brand-black/90 backdrop-blur-xl border-t border-white/[0.08]" />

      <div className="relative px-4 py-4 space-y-3">
        {/* Top row */}
        <div className="flex items-center gap-3">
          {/* App icon */}
          <div className="w-12 h-12 rounded-2xl bg-brand-lime flex items-center justify-center shrink-0 shadow-lime-sm">
            <span className="text-brand-black font-black text-base tracking-tight">SW</span>
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-brand-white">Add to Home Screen</p>
            {platform === 'ios' ? (
              <p className="text-xs text-brand-gray mt-0.5 leading-snug">
                Tap <Share2 size={11} className="inline mx-0.5 text-[#1C9CF6]" strokeWidth={2.5} /> below then{' '}
                <span className="text-brand-gray-light font-medium">&quot;Add to Home Screen&quot;</span>
              </p>
            ) : (
              <p className="text-xs text-brand-gray mt-0.5">
                Install for the best experience — works offline too
              </p>
            )}
          </div>

          {platform === 'android' && prompt && (
            <button
              onClick={install}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-brand-lime text-brand-black text-xs font-bold shrink-0 active:scale-95 transition-transform"
            >
              <Download size={13} />
              Install
            </button>
          )}

          <button
            onClick={dismiss}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-brand-gray hover:text-brand-white transition-colors shrink-0 ml-1"
            aria-label="Dismiss install banner"
          >
            <X size={15} />
          </button>
        </div>

        {/* iOS step-by-step */}
        {platform === 'ios' && (
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-0.5">
            {[
              { n: '1', text: 'Tap Share' },
              { n: '2', text: 'Scroll down' },
              { n: '3', text: '"Add to Home Screen"' },
              { n: '4', text: 'Tap Add' },
            ].map(({ n, text }, i, arr) => (
              <React.Fragment key={n}>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="w-5 h-5 rounded-full bg-brand-lime/15 border border-brand-lime/30 text-brand-lime text-[10px] font-bold flex items-center justify-center">
                    {n}
                  </span>
                  <span className="text-[11px] text-brand-gray-light whitespace-nowrap">{text}</span>
                </div>
                {i < arr.length - 1 && (
                  <span className="text-brand-gray/30 text-xs shrink-0">›</span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
